import * as React from 'react';
import { useState, useEffect } from 'react';
import * as Livestream from '../Types/Livestream';
import Perspective from '../Types/Perspective';
import { htmlIf, maybeHtml } from '../Utils/HTML';
import { Package } from '../Types/Package';
import { formatUSDWithoutCents } from '../Utils/Currency';

type Props =
  { packages: Package[]
  }

const PackageGrid = (props: Props) => {
  const [searchText, setSearchText] = useState('');
  const [filteredPackages, setFilteredPackages] = useState(props.packages);

  // This hook serves to re-sort and re-filter the list of packages when the search text is changed
  useEffect(() => {
    const filtered = props.packages.filter((pkg) => {
      const searchFields = [
        pkg.title,
        pkg.description,
        pkg.copilotIndividual?.preferredName
      ];
      const tagNames = pkg.tags.map((tag) => tag.name);
      return (
        searchFields.some((field) =>
          field?.toLowerCase().includes(searchText.toLowerCase())
        ) ||
        tagNames.some((tagName) =>
          tagName?.toLowerCase().includes(searchText.toLowerCase())
        )
      );
    });

    setFilteredPackages(filtered);
  }, [searchText])

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchText(query);
  }

  const PackageCard = (pkg: Package) => {
    return (
      <a className="text-decoration-none" href={pkg.canonicalPath}>
        <div className={`card h-100 t--package-card-for-id-${pkg.id}`}>
          {maybeHtml(pkg.imageUrl, (imageUrl) => (
            <img src={imageUrl} className="card-img-top" loading='lazy' />
          ))}
          <div className="card-body d-flex flex-column">
            <h6 className={`card-title ${pkg.tags?.length === 0 ? 'mb-1' : 'mb-0'}`}>{pkg.title}</h6>
            {maybeHtml(pkg.copilotIndividual, (copilot) => (
              <div className="d-flex align-items-center mt-1 mb-1">
                <div className="avatar avatar-sm">
                  <img className="avatar-img border border-white border-4 border-card rounded-circle" src={copilot.imageUrl} loading='lazy' />
                </div>
                <div className="ms-1">
                  <div className="fs-sm">
                    Created by {copilot.preferredName}
                    {maybeHtml(copilot.averageRating, (rating) => (
                      <div className="mt-0">
                        <i className="ai-star me-1" />
                        {rating}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
            {htmlIf(pkg.tags?.length > 0,
              <div className="d-flex align-items-center flex-wrap mt-1 mb-2">
                {pkg.tags?.map((tag) => (
                  <div className="badge bg-accent badge-sm text-dark me-1 d-inline-block mt-1">{tag.name}</div>
                ))}
              </div>
            )}
            <div className="text-dark fw-bold mt-auto">
              { pkg.hourlyRateCents
              ? `From ${formatUSDWithoutCents(pkg.hourlyRateCents)}/hour`
              : `From ${formatUSDWithoutCents(pkg.basicAmountCents)}`
              }
            </div>
          </div>
        </div>
      </a>
    )
  }

  return (
    <>
      <div className="row mt-3 align-items-center">
        <div className="col-md-8">
          <div className="input-group align-items-center p-0 bg-white">
            <div className="input-group-prepend ms-2">
              <i className="ai-search"></i>
            </div>
            <input
              className="form-control"
              type="search"
              value={searchText}
              onChange={handleSearchInputChange}
              placeholder="Search by title, description, tag, or CoPilot…"
            />
          </div>
        </div>
      </div>
      <div className="row">
        {filteredPackages.map((pkg) => (
        <div className="col-lg-4 mt-3" key={pkg.id}>
            {PackageCard(pkg)}
        </div>
        ))}
      </div>
    </>
  )
}

export default PackageGrid;
